import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {placementSchema} from '../placementsManagementTool.schema';
import getPlacementKey from '../utils/getPlacementKey';
import {Placement_UI} from 'common/src/modules/campaignPlacements/placements.types';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import runPlacementsValidation from '../utils/runPlacementsValidation';
import {campaignPlacementsApi} from 'common/src/modules/campaignPlacements/campaignPlacements.api';
import convertPlacementSchemaToPlacementUI from '../utils/convertPlacementSchemaToPlacementUI';
import {CLStore} from 'common/src/redux/store';
import fetchCampaignPlacements from 'common/src/modules/campaigns/store/campaignTree/thunks/fetchCampaignPlacements';
import {clinchEnums, featureFlags} from '@cl/shared';
import {CLEnumType} from 'common/src/modules/enums/enums.types';

const editPlacementsToolSchema = z.object({
  placements: z.array(placementSchema),
});

export default function editPlacementsTool(store: CLStore) {
  async function execute(params: ToolExecuteParams<typeof editPlacementsToolSchema>) {
    const campaignId = store.getState().campaignPage.campaignTree.campaign?.id ?? 0;
    const campaignVersionId =
      store.getState().campaignPage.campaignTree.campaignVersions[0]?.id ?? 0;

    if (!campaignId || !campaignVersionId) {
      return toolError('Campaign or campaign version not found');
    }

    const {input} = params;
    const {placements} = input;
    const existingPlacements = store.getState().campaignPage.campaignTree.campaignPlacements;
    const campaignType = store.getState().campaignPage.campaignTree.campaign?.type;
    const campaignEnums = clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage];
    const enabledFeatureFlags = featureFlags.enabledFlagsSubject.data;

    const existingPlacementsByKeys = existingPlacements.reduce(
      (acc, placement) => {
        acc[getPlacementKey(placement.placementId, placement.dspId)] = placement;

        return acc;
      },
      {} as Record<string, Placement_UI>,
    );

    // Group placements by their key
    const placementsByKeys = placements.reduce(
      (acc, placement) => {
        acc[getPlacementKey(placement.plcId, placement.dspId)] = placement;

        return acc;
      },
      {} as Record<string, z.infer<typeof placementSchema>>,
    );

    const overlappingPlacements = Object.entries(placementsByKeys).filter(
      ([key]) => existingPlacementsByKeys[key],
    );

    if (overlappingPlacements.length !== placements.length) {
      return toolError(
        `The following placements are not found in the campaign: ${JSON.stringify(placements.filter((placement) => !Object.keys(overlappingPlacements).includes(getPlacementKey(placement.plcId, placement.dspId))))}`,
      );
    }

    const validationResults = await runPlacementsValidation({
      placements,
      campaignType,
      campaignEnums,
      featureFlags: enabledFeatureFlags,
    });

    if (validationResults.length > 0) {
      return toolError(
        `The following placements have validation errors: ${JSON.stringify(validationResults)}`,
      );
    }

    await Promise.all(
      placements.map(async (placement) => {
        const placementUI = convertPlacementSchemaToPlacementUI(placement, campaignEnums);

        if (!placementUI) {
          return;
        }

        placementUI.id =
          existingPlacementsByKeys[getPlacementKey(placement.plcId, placement.dspId)].id;

        await campaignPlacementsApi.editPlacement_v2({
          campaignId,
          campaignVersionId,
          placement: placementUI as Placement_UI,
        });
      }),
    ).then(() => {
      store.dispatch(fetchCampaignPlacements({campaignId, campaignVersionId}));
    });

    return toolResult(JSON.stringify(placements));
  }

  return tool({
    name: 'editPlacements',
    displayName: 'Editing placements',
    description: `Edit placements in the campaign. 

Available fields:
- plcId (string, required): Placement ID
- name (string, required): Placement name  
- dspId (number, required): DSP ID
- adSize (string, optional): Ad size like "300x250" or "All"
- environment (enum, required): Campaign environment
- customParameters (object, optional): Custom parameters as key-value pairs

Custom parameters support:
- Use parameter names like 'StartDateTime', 'EndDateTime', 'animationLength', 'RequirePingForAttribution', etc.
- Values can be strings, DateTime objects, or booleans
- Example: { "StartDateTime": <DateTime>, "EndDateTime": <DateTime>, "animationLength": "5" }`,
    inputSchema: editPlacementsToolSchema,
    execute,
  });
}
